// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { IconPresentationProps } from "@insite/mobius/Icon";
import xIconsObject from "@synd/XDL/Icons/xngCommonIcons";
import breakpoints from "@synd/XDL/PreStyle/breakpoints";
import systemZIndex from "@synd/XDL/PreStyle/zIndex";
import isDate from "lodash/isDate";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => { };

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const getVideoEmbeddedUrl = (src: string) => {
    if (!src) {
        return;
    }
    if (src.includes("vimeo")) {
        return src;
    }
    const srcParts = src.split("/");
    const videoId = srcParts.pop();
    return `https://www.youtube.com/embed/${videoId}`;
};

export const safeJsonParse = (str: string) => {
    try {
        return JSON.parse(str);
    } catch {
        return str;
    }
};

export const isTouchDevice = () => {
    if (typeof window === "undefined") {
        return false;
    }
    return !!window.ontouchstart || navigator?.maxTouchPoints > 0 || (navigator as any)?.msMaxTouchPoints > 0;
};

export const generateSlidesPerViewMap = (options?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    if (!options) {
        values[maxWidths[0]] = {
            slidesPerView: 4,
        };
        values[maxWidths[1]] = {
            slidesPerView: 4,
        };
        values[maxWidths[2]] = {
            slidesPerView: 6,
        };
        values[maxWidths[3]] = {
            slidesPerView: 6,
        };
        values[maxWidths[4]] = {
            slidesPerView: 6,
        };
        return values;
    }

    Object.keys(options).forEach(key => {
        if (!maxWidths[+key]) {
            return;
        }
        values[maxWidths[+key]] = {
            slidesPerView: +options[+key],
        };
    });
    return values;
};

export const preloadImage = (src: string) => {
    if (!src) {
        return;
    }
    if (typeof document !== "undefined" && !document.querySelector(`link[href="${src}"]`)) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = src;
        document.head.appendChild(link);
    }
};

export const isDateExpired = (date: Date | null) => {
    if (!date) {
        return false;
    }
    const expirationDate = new Date(date);
    const currentDate = new Date();
    expirationDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    return expirationDate <= currentDate;
};

export const delay = (time = 100) => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(true);
        }, time);
    });
};

export const compareAlphaNumaric = (str1: string, str2: string) => {
    const aParts = str1.split("");
    const bParts = str2.split("");
    for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
        const aPart = aParts[i];
        const bPart = bParts[i];
        const isNan = Number.isNaN(parseInt(aPart, 10)) || Number.isNaN(parseInt(bPart, 10));
        if (isNan) {
            if (aPart.localeCompare(bPart)) {
                return aPart.localeCompare(bPart);
            }
        } else {
            // Compare as numbers
            const aNum = parseInt(aPart, 10);
            const bNum = parseInt(bPart, 10);
            if (aNum < bNum) {
                return -1;
            }
            if (aNum > bNum) {
                return 1;
            }
        }
    }

    return aParts.length - bParts.length;
};

/**
 *
 * we have used this equation vw=(fontSize/viewportWidth) × 100 to adjust font size on tablet screen
 * @param mobile
 * @param tablet
 * @param desktop
 * @returns
 */
export const autoClamp = (mobile: number, tablet: number, desktop: number) => {
    const vw = ((tablet / breakpoints.values[2]) * 100).toFixed(2);
    return `clamp(${mobile}px ,${vw}vw , ${desktop}px)`;
};

export const getIcon = (icon: string, size: number): IconPresentationProps | undefined => {
    const src = xIconsObject[icon];
    return src ? { src, size } : undefined;
};

export const getYoutubeThumbnail = (url: string) => {
    // This regex matches all youtube urls ( embed, shortlinks, full-url )
    if (!url) {
        return "";
    }
    const match = url.match(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|shorts\/|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
    );

    if (!match?.length) {
        return "";
    }
    const videoId = match[6];
    return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

export const getwishListLoadingTime = (wishList: any) => {
    const wishListLinesCount = wishList?.wishListLinesCount || 0;
    if (wishListLinesCount <= 20) {
        return 5;
    }
    if (wishListLinesCount <= 100) {
        return 15;
    }
    return 30;
}